import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { ExperienceListFilters } from 'actions/ExperienceActions'
import LoyaltyLoungePassModal from './LoyaltyLoungePassModal'
import getLoyaltyLoungePassItemViews from 'checkout/selectors/view/itemViews/getLoyaltyLoungePassItemViews'
import LoyaltyBenefitsLoungeItem from './LoyaltyBenefitsLoungeItem'
import LoyaltyBenefitCompactRow from './LoyaltyBenefitCompactRow'
import getLoyaltyOrderViewLoungePassFilters from 'selectors/orderView/getLoyaltyOrderViewLoungePassFilters'
import { nonNullable, unique } from 'lib/array/arrayUtils'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import getOrderAccommodationStartDate from 'lib/order/getOrderAccommodationStartDate'
import getOrderAccommodationEndDate from 'lib/order/getOrderAccommodationEndDate'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

interface Props {
  order: App.Order
  layout?: 'tile' | 'row'
  /** Per-order lounge availability filters; falls back to the route-viewed order's filters. */
  availabilityFilters?: Array<ExperienceListFilters>
}

function LoyaltyOrderViewBenefitsLoungeItem({ order, layout = 'tile', availabilityFilters: availabilityFiltersProp }: Props) {
  const intl = useIntl()
  const views = useAppSelector(state => getLoyaltyLoungePassItemViews(state).data)
  const cartOrderId = useAppSelector(state => state.checkout.cart.existingOrder?.id)
  const showModal = useContext(ModalContext)

  const viewedOrderFilters = useAppSelector(state => getLoyaltyOrderViewLoungePassFilters(state))
  const availabilityFilters = availabilityFiltersProp ?? viewedOrderFilters

  const orderLocationsCountryCodes = useMemo(() => {
    return nonNullable(unique(order.items.map(item => item.offer.property?.geoData?.countryCode)))
  }, [order])

  const dispatch = useAppDispatch()
  const onAddItems = useCallback((items: Array<App.Checkout.LoyaltyLoungePassItem>) => {
    dispatch(addToCheckoutCart(items, {
      regionCode: order.regionCode,
      currencyCode: order.currencyCode,
      order,
      mode: 'loyalty-redeem-benefits',
    }))
    dispatch(navigateToCheckout())
  }, [dispatch, order])

  const checkInDate = getOrderAccommodationStartDate(order)
  const checkOutDate = getOrderAccommodationEndDate(order)
  const flightDates = useMemo(() => order.flightItems?.map(item => ({
    departingAirport: item.departingAirport,
    arrivalAirport: item.arrivalAirport,
    departureDate: item.departureDate,
    arrivalDate: item.arrivalDate,
  })), [order.flightItems])

  const onRedeem = useCallback(() => {
    showModal(<LoyaltyLoungePassModal
      availabilityFilters={availabilityFilters}
      locationsCountryCodes={orderLocationsCountryCodes}
      onAddItems={onAddItems}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      flightDates={flightDates}
    />)
  }, [showModal, availabilityFilters, orderLocationsCountryCodes, onAddItems, checkInDate, checkOutDate, flightDates])

  const onEdit = useCallback(() => {
    showModal(<LoyaltyLoungePassModal
      defaultStep="tickets"
      loungePassViews={views}
      availabilityFilters={availabilityFilters}
      locationsCountryCodes={orderLocationsCountryCodes}
      onAddItems={onAddItems}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      flightDates={flightDates}
    />)
  }, [showModal, views, availabilityFilters, orderLocationsCountryCodes, onAddItems, checkInDate, checkOutDate, flightDates])

  if (layout === 'row') {
    // Only treat cart lounge passes as "added" when the cart belongs to this order.
    const hasAddedForThisOrder = cartOrderId === order.id && views.length > 0
    const editAction = <TextButton kind="secondary" variant="dark" size="small" shape="round" startIcon={<LineEditAltIcon />} onClick={onEdit}>
      {intl.formatMessage({ id: 'cp4eOp-RVFNlSi_b_ff5', defaultMessage: 'Edit', description: 'Action button label to edit lounge pass selection in the loyalty program section' })}
    </TextButton>
    const addAction = <TextButton kind="secondary" variant="dark" size="small" shape="round" startIcon={<LinePlusIcon />} onClick={onRedeem}>
      <CSSBreakpoint as="span" max="mobile">
        {intl.formatMessage({ id: 'tSyiNkLzkGgaIKhSivRH', defaultMessage: 'Add', description: 'Short add-to-booking button label shown on mobile devices' })}
      </CSSBreakpoint>
      <CSSBreakpoint as="span" min="tablet">
        {intl.formatMessage({ id: 'iZfWZcvSob6VJ3UC00LW', defaultMessage: 'Add to booking', description: 'Action button label to add a loyalty benefit to an upcoming booking' })}
      </CSSBreakpoint>
    </TextButton>
    return <LoyaltyBenefitCompactRow
      benefitType="lounge_pass"
      heading={intl.formatMessage({
        id: 'zSPXAvw6egjQ1SuGwY-r',
        defaultMessage: 'Free airport lounge passes',
        description: 'Heading for lounge pass benefit tile in the loyalty benefits section',
      })}
      action={hasAddedForThisOrder ? editAction : addAction}
    />
  }

  return <LoyaltyBenefitsLoungeItem
    views={views}
    onEdit={onEdit}
    onRedeem={onRedeem}
  />
}

export default LoyaltyOrderViewBenefitsLoungeItem
